import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Cloud, Rocket, BrainCircuit } from "lucide-react";
import Reveal from "@/components/Reveal";

const PILLARS = [
  {
    id: "cloud-adoption",
    title: "Cloud Adoption",
    Icon: Cloud,
    to: "/solutions/cloud-strategy",
    image:
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1400&q=80",
    description:
      "Assess, migrate and operate across Google Cloud, Microsoft Azure and Alibaba Cloud — so you adopt the cloud with confidence, security and measurable ROI.",
  },
  {
    id: "technology-innovation",
    title: "Technology Innovation",
    Icon: Rocket,
    to: "/solutions/application-modernization",
    image:
      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1400&q=80",
    description:
      "Modernize applications, data and infrastructure with cloud-native engineering that turns legacy constraints into speed, scale and resilience.",
  },
  {
    id: "ai-web3-development",
    title: "AI / Web3 Development",
    Icon: BrainCircuit,
    to: "/solutions/ai-data",
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1400&q=80",
    description:
      "Design and build intelligent, next-generation experiences — from generative AI and analytics to secure, decentralized Web3 solutions.",
  },
];

function PillarCard({ pillar }) {
  const { Icon } = pillar;
  return (
    <Link
      to={pillar.to}
      data-testid={`pillar-card-${pillar.id}`}
      className="group relative isolate flex min-h-[440px] flex-col justify-end overflow-hidden rounded-2xl border border-border shadow-card transition-transform duration-200 hover:-translate-y-1 hover:shadow-cardHover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      {/* Photo-realistic background image (hero-slide style) */}
      <img
        src={pillar.image}
        alt={pillar.title}
        loading="lazy"
        className="absolute inset-0 -z-10 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      {/* Readability overlays blended like the hero slides */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-cbink/95 via-cbink/55 to-cbink/10" aria-hidden="true" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-tr from-primary/25 via-transparent to-cbgreen/20" aria-hidden="true" />

      <div className="p-7">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/25 bg-white/15 text-white backdrop-blur-sm">
          <Icon size={24} />
        </span>
        <h3 className="mt-5 font-display text-2xl font-extrabold text-white">{pillar.title}</h3>
        <p className="mt-3 text-[15px] leading-relaxed text-white/85">{pillar.description}</p>
        <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white">
          Learn more
          <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

export default function ServicePillars() {
  return (
    <section className="bg-white py-20 lg:py-24" data-testid="service-pillars">
      <div className="container-cb">
        <div className="max-w-2xl">
          <p className="eyebrow">What We Do</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold text-cbink sm:text-4xl">
            Built for how modern businesses grow
          </h2>
          <p className="mt-3 text-[17px] leading-relaxed text-cbslate">
            Three focused practices — from cloud adoption to next-generation innovation — that help
            you move faster, operate smarter and build what comes next.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map((p, i) => (
            <Reveal key={p.id} delay={i * 80}>
              <PillarCard pillar={p} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
